import React, { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { X, Save } from 'lucide-react'
import { Button } from '../../ui/Button'
import { Input } from '../../ui/Input'
import { Textarea } from '../../ui/Textarea'
import { Switch } from '../../ui/switch'
import { planManagementApi, type SubscriptionPlan, type UpdatePlanRequest } from '../../../services/planManagementApi'

interface EditPlanModalProps {
  plan: SubscriptionPlan
  onClose: () => void
}

export function EditPlanModal({ plan, onClose }: EditPlanModalProps) {
  const [formData, setFormData] = useState({
    name: plan.name,
    display_name: plan.display_name,
    description: plan.description || '',
    price_monthly_dollars: (plan.price_monthly / 100).toFixed(2),
    price_yearly_dollars: plan.price_yearly ? (plan.price_yearly / 100).toFixed(2) : '',
    messages_limit: plan.messages_limit,
    credits_limit: (plan as any).credits_limit ?? -1,
    ai_budget_usd: (plan as any).ai_budget_usd ?? 0,
    cost_multiplier: (plan as any).cost_multiplier ?? 3,
    team_members_limit: plan.team_members_limit,
    chatbots_limit: plan.chatbots_limit,
    storage_gb: (plan.storage_limit_mb / 1024).toString(),
    teams_limit: (plan as any).teams_limit ?? '',
    is_active: plan.is_active,
    sort_order: plan.sort_order
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdatePlanRequest }) =>
      planManagementApi.updatePlan(id, updates),
    onSuccess: () => {
      onClose()
    },
    onError: (error: any) => {
      console.error('Failed to update plan:', error)
      setErrors({ submit: error.response?.data?.detail || 'Failed to update plan' })
    }
  })

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const updates: UpdatePlanRequest = {
      name: formData.name,
      display_name: formData.display_name,
      description: formData.description || undefined,
      price_monthly: Math.round(parseFloat(formData.price_monthly_dollars) * 100),
      price_yearly: formData.price_yearly_dollars
        ? Math.round(parseFloat(formData.price_yearly_dollars) * 100)
        : undefined,
      messages_limit: formData.messages_limit,
      credits_limit: formData.credits_limit,
      ai_budget_usd: formData.ai_budget_usd,
      cost_multiplier: formData.cost_multiplier,
      team_members_limit: formData.team_members_limit,
      chatbots_limit: formData.chatbots_limit,
      storage_limit_mb: Math.round(parseFloat(formData.storage_gb) * 1024),
      teams_limit: formData.teams_limit === '' ? undefined : parseInt(String(formData.teams_limit)) || 0,
      is_active: formData.is_active,
      sort_order: formData.sort_order
    }

    updateMutation.mutate({ id: plan.id, updates })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Edit Plan: {plan.display_name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name *
              </label>
              <Input
                value={formData.display_name}
                onChange={(e) => handleChange('display_name', e.target.value)}
                placeholder="e.g., Professional Plan"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plan Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value.toLowerCase())}
                placeholder="e.g., professional"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
            />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Price ($)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price_monthly_dollars}
                onChange={(e) => handleChange('price_monthly_dollars', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yearly Price ($)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price_yearly_dollars}
                onChange={(e) => handleChange('price_yearly_dollars', e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Usage Limits */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Usage Limits (-1 for unlimited)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AI Credits Budget (USD)
                </label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.ai_budget_usd ?? 0}
                  onChange={(e) => handleChange('ai_budget_usd', parseFloat(e.target.value) || 0)}
                />
                <p className="mt-1 text-xs text-gray-500">User-facing budget per month in $. 0 = unlimited.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cost Multiplier
                </label>
                <Input
                  type="number"
                  min="1"
                  step="0.5"
                  value={formData.cost_multiplier ?? 3}
                  onChange={(e) => handleChange('cost_multiplier', parseFloat(e.target.value) || 3)}
                />
                <p className="mt-1 text-xs text-gray-500">User pays: actual AI cost × this. Default 3×.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agents
                </label>
                <Input
                  type="number"
                  min="-1"
                  value={formData.chatbots_limit}
                  onChange={(e) => handleChange('chatbots_limit', parseInt(e.target.value) || 0)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team Members
                </label>
                <Input
                  type="number"
                  min="-1"
                  value={formData.team_members_limit}
                  onChange={(e) => handleChange('team_members_limit', parseInt(e.target.value) || 0)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teams
                </label>
                <Input
                  type="number"
                  min="-1"
                  value={formData.teams_limit}
                  onChange={(e) => handleChange('teams_limit', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <Input
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) => handleChange('sort_order', parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Active Plan</label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => handleChange('is_active', checked)}
              />
            </div>
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
